<template>
  <Teleport to="body">
    <Transition name="call-screen-fade">
      <div v-if="callStore.showCallScreen" class="call-screen-overlay">
        <div class="call-screen-content">
          <!-- Status + remote name -->
          <div class="call-status-wrap">
            <p class="call-status" :class="{ 'call-status--pulse': isWaiting }">
              <template v-if="callStore.callState === callStore.CALL_STATES.DIALING">
                {{ $t('chat.dialing') }}
              </template>
              <template v-else-if="callStore.callState === callStore.CALL_STATES.RINGING">
                {{ $t('chat.ringing') }}
              </template>
              <template v-else-if="callStore.callState === callStore.CALL_STATES.CONNECTING">
                {{ $t('chat.connecting') }}
              </template>
              <template v-else-if="callStore.callState === callStore.CALL_STATES.RECONNECTING">
                {{ $t('chat.reconnecting') || 'Reconnecting...' }}
              </template>
              <template v-else-if="callStore.callState === callStore.CALL_STATES.CONNECTED">
                <!-- When connected but not yet accepted, still show "Connecting..." -->
                <template v-if="!callStore.isAccepted">
                  {{ $t('chat.connecting') || 'Connecting...' }}
                </template>
                <!-- Voice (no video tracks): timer in the main status; immersive video uses bottom bar -->
                <template v-else-if="!showImmersiveVideo && connectedDuration">
                  {{ connectedDuration }}
                </template>
              </template>
              <template v-else>
                {{ $t('chat.callEnded') || 'Call ended' }}
              </template>
            </p>
            <!-- When not using fullscreen video layout, show name under status -->
            <p v-if="callStore.remoteDisplayName && !showImmersiveVideo" class="call-status-sub">
              {{ callStore.remoteDisplayName }}
            </p>
          </div>

          <!-- Remote audio (hidden): must be in DOM so remote voice is heard -->
          <audio ref="remoteAudioEl" autoplay playsinline class="call-remote-audio" />
          <button
            v-if="showUnmutePrompt"
            type="button"
            class="call-unmute-banner"
            @click="handleTapToUnmute"
          >
            {{ $t('chat.tapToUnmute') || 'Tap to hear audio' }}
          </button>

          <!-- Fullscreen video: native video call, or voice call with local/remote camera -->
          <template v-if="showImmersiveVideo">
            <div class="call-video-container">
              <div class="remote-video-wrapper">
                <video ref="remoteVideoEl" autoplay playsinline class="remote-video" />
                <div v-if="!callStore.remoteVideoTrack" class="remote-placeholder">
                  <div class="remote-placeholder-avatar">
                    <img v-if="callStore.remoteAvatarUrl" :src="callStore.remoteAvatarUrl" alt="" class="w-100 h-100 object-fit-cover" />
                    <VsxIcon v-else iconName="User" :size="48" type="bold" color="#fff" />
                  </div>
                  <span class="remote-placeholder-name">{{ callStore.remoteDisplayName }}</span>
                </div>
              </div>
              <div class="local-video-pip">
                <video
                  ref="localVideoEl"
                  autoplay
                  playsinline
                  muted
                  class="w-100 h-100 object-fit-cover local-video-mirror"
                />
                <div v-if="!callStore.localVideoTrack" class="local-placeholder">{{ $t('chat.you') }}</div>
              </div>
            </div>
            <!-- Name + timer over fullscreen video -->
            <div class="call-video-bottom">
              <div v-if="callStore.remoteDisplayName" class="call-video-name">
                {{ callStore.remoteDisplayName }}
              </div>
              <div
                v-if="(callStore.callState === callStore.CALL_STATES.CONNECTED || callStore.callState === callStore.CALL_STATES.RECONNECTING) && callStore.isAccepted && connectedDuration"
                class="call-video-timer"
              >
                {{ connectedDuration }}
              </div>
            </div>
          </template>

          <!-- Voice only: avatar with optional ring when waiting -->
          <template v-else>
            <div class="call-voice-avatar-wrapper">
              <div v-if="isWaiting" class="call-voice-rings" aria-hidden="true">
                <span class="voice-ring voice-ring-1"></span>
                <span class="voice-ring voice-ring-2"></span>
              </div>
              <div class="call-voice-avatar">
                <img v-if="callStore.remoteAvatarUrl" :src="callStore.remoteAvatarUrl" alt="" class="w-100 h-100 object-fit-cover" />
                <VsxIcon v-else iconName="User" :size="64" type="bold" color="#fff" />
              </div>
            </div>
          </template>

          <!-- Controls -->
          <div class="call-controls">
            <template v-if="callStore.callState === callStore.CALL_STATES.CONNECTED || callStore.callState === callStore.CALL_STATES.RECONNECTING">
              <button type="button" class="call-btn" :class="callStore.isVideoEnabled ? 'call-btn--secondary' : 'call-btn--muted'"
                @click="callStore.toggleVideo()"
                :title="callStore.isVideoEnabled ? $t('chat.turnOffVideo') : $t('chat.turnOnVideo')"
                :aria-label="callStore.isVideoEnabled ? $t('chat.turnOffVideo') : $t('chat.turnOnVideo')">
                <VsxIcon :iconName="callStore.isVideoEnabled ? 'Video' : 'VideoSlash'" :size="24" type="bold" :color="callStore.isVideoEnabled ? '#fff' : '#fecaca'" />
              </button>
              <button type="button" class="call-btn" :class="callStore.isMuted ? 'call-btn--muted' : 'call-btn--secondary'"
                @click="callStore.toggleMute()"
                :title="callStore.isMuted ? $t('chat.unmute') : $t('chat.mute')"
                :aria-label="callStore.isMuted ? $t('chat.unmute') : $t('chat.mute')">
                <VsxIcon :iconName="callStore.isMuted ? 'MicrophoneSlash' : 'Microphone2'" :size="24" type="bold" :color="callStore.isMuted ? '#fecaca' : '#fff'" />
              </button>
            </template>

            <button type="button" class="call-btn call-btn--end" @click="hangUp" :aria-label="$t('chat.endCall')" :title="$t('chat.endCall')">
              <VsxIcon iconName="CallSlash" :size="28" type="bold" color="#fff" />
            </button>
          </div>

        <!-- Outgoing call ringtone (hidden) -->
        <audio
          ref="outgoingRingtoneEl"
          :src="outgoingRingtoneUrl"
          loop
          preload="auto"
        />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from 'vue';
import { VsxIcon } from 'vue-iconsax';
import { useCallStore } from '@/domains/chat/store/callStore';
import outgoingRingtoneUrl from '@/assets/sounds/outgoing_call.mp3';

const callStore = useCallStore();

/** Fullscreen video UI: scheduled video calls, or audio call with any published video. */
const showImmersiveVideo = computed(() => {
  if (callStore.isVideoCall) return true;
  const s = callStore.callState;
  return (
    (s === callStore.CALL_STATES.CONNECTED || s === callStore.CALL_STATES.RECONNECTING) &&
    callStore.isAccepted &&
    (!!callStore.localVideoTrack || !!callStore.remoteVideoTrack)
  );
});

const remoteVideoEl = ref(null);
const localVideoEl = ref(null);
const remoteAudioEl = ref(null);
const outgoingRingtoneEl = ref(null);

const isOutgoingDialing = ref(false);
const isWaiting = ref(false);
const connectedDuration = ref('');
const showUnmutePrompt = ref(false);
let durationTimer = null;
let outgoingToneActive = false;

const isOutgoingRinging = computed(() => {
  const s = callStore.callState;
  return (
    (s === callStore.CALL_STATES.DIALING || s === callStore.CALL_STATES.RINGING) &&
    !callStore.session?.isIncoming &&
    !callStore.isAccepted
  );
});

watch(
  () => [callStore.callState, callStore.isAccepted],
  ([s, accepted]) => {
    isOutgoingDialing.value =
      (s === callStore.CALL_STATES.DIALING || s === callStore.CALL_STATES.RINGING) &&
      !callStore.session?.isIncoming;
    isWaiting.value =
      s === callStore.CALL_STATES.DIALING ||
      s === callStore.CALL_STATES.RINGING ||
      s === callStore.CALL_STATES.CONNECTING ||
      s === callStore.CALL_STATES.RECONNECTING ||
      (s === callStore.CALL_STATES.CONNECTED && !accepted);

    // Outgoing ringtone control: play tone while we are the caller and the call
    // is dialing/ringing but not yet accepted.
    const el = outgoingRingtoneEl.value;
    if (!el) return;
    const shouldPlayTone = isOutgoingRinging.value;
    if (shouldPlayTone && !outgoingToneActive) {
      el.currentTime = 0;
      const maybePromise = el.play && el.play();
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.catch(() => {
          // Ignore autoplay failures; visual UI still indicates outgoing call.
        });
      }
      outgoingToneActive = true;
    } else if (!shouldPlayTone && outgoingToneActive) {
      if (el.pause) {
        el.pause();
      }
      el.currentTime = 0;
      outgoingToneActive = false;
    }

    if ((s === callStore.CALL_STATES.CONNECTED || s === callStore.CALL_STATES.RECONNECTING) && accepted) {
      if (!durationTimer) {
        const start = Date.now();
        const format = (ms) => {
          const totalSec = Math.floor(ms / 1000);
          const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
          const sec = String(totalSec % 60).padStart(2, '0');
          return `${m}:${sec}`;
        };
        connectedDuration.value = format(0);
        durationTimer = setInterval(() => {
          connectedDuration.value = format(Date.now() - start);
        }, 1000);
      }
    } else {
      if (durationTimer) {
        clearInterval(durationTimer);
        durationTimer = null;
      }
      if (s === callStore.CALL_STATES.ENDED || s === callStore.CALL_STATES.IDLE) {
        connectedDuration.value = '';
      }
    }
  },
  { immediate: true }
);

function hangUp() {
  if (callStore.callState === callStore.CALL_STATES.CONNECTED || callStore.callState === callStore.CALL_STATES.RECONNECTING) {
    callStore.endCall();
  } else {
    callStore.cancelCall();
  }
}

function attachTrack(element, track) {
  if (!element || !track) return;
  track.attach(element);
  // Best-effort: try to play immediately; if blocked by browser autoplay policy,
  // we will show a "Tap to unmute" prompt so user can trigger audio via gesture.
  if (element === remoteAudioEl.value) {
    showUnmutePrompt.value = false;
    const maybePromise = element.play && element.play();
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.catch(() => {
        showUnmutePrompt.value = true;
      });
    }
  }
}

function detachTrack(element) {
  if (!element) return;
  try {
    element.srcObject = null;
  } catch (_) {
    /* ignore */
  }
}

function handleTapToUnmute() {
  const el = remoteAudioEl.value;
  if (!el) return;
  const maybePromise = el.play && el.play();
  if (maybePromise && typeof maybePromise.then === 'function') {
    maybePromise
      .then(() => {
        showUnmutePrompt.value = false;
      })
      .catch(() => {
        showUnmutePrompt.value = true;
      });
  } else {
    showUnmutePrompt.value = false;
  }
}

onMounted(() => {
  const stopLocal = watch(() => callStore.localVideoTrack, (track) => {
    if (track) attachTrack(localVideoEl.value, track);
    else detachTrack(localVideoEl.value);
  }, { immediate: true });
  const stopRemoteVideo = watch(() => callStore.remoteVideoTrack, (track) => {
    if (track) attachTrack(remoteVideoEl.value, track);
    else detachTrack(remoteVideoEl.value);
  }, { immediate: true });
  const stopRemoteAudio = watch(() => callStore.remoteAudioTrack, (track) => {
    if (track) attachTrack(remoteAudioEl.value, track);
    else detachTrack(remoteAudioEl.value);
  }, { immediate: true });
  onUnmounted(() => {
    if (durationTimer) {
      clearInterval(durationTimer);
      durationTimer = null;
    }
    stopLocal();
    stopRemoteVideo();
    stopRemoteAudio();
    detachTrack(remoteVideoEl.value);
    detachTrack(localVideoEl.value);
    detachTrack(remoteAudioEl.value);
  });
});
</script>

<style scoped>
/* BASE OVERLAY */
.call-screen-overlay {
  position: fixed;
  inset: 0;
  background-color: #050505;
  background-image: 
    radial-gradient(circle at 20% 0%, rgba(var(--color-brand-rgb), 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 100%, rgba(var(--color-brand-rgb), 0.15) 0%, transparent 50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: 1.5rem;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.call-screen-fade-enter-active,
.call-screen-fade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.call-screen-fade-enter-from,
.call-screen-fade-leave-to {
  opacity: 0;
  transform: scale(1.05);
}

.call-screen-content {
  width: 100%;
  max-width: 480px;
  height: 100%;
  max-height: 850px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: call-content-in 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  position: relative;
}

@keyframes call-content-in {
  0% { opacity: 0; transform: translateY(30px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

/* STATUS AREA */
.call-status-wrap {
  margin-bottom: 2.5rem;
  text-align: center;
  min-height: 4rem;
  z-index: 10;
}
.call-status {
  font-size: 1.75rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}
.call-status--pulse {
  animation: txt-pulse 2s ease-in-out infinite;
}
@keyframes txt-pulse {
  0%, 100% { opacity: 1; text-shadow: 0 0 15px rgba(255,255,255,0.3); }
  50% { opacity: 0.6; text-shadow: none; }
}
.call-status-sub {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0 0;
  font-weight: 500;
}

/* VIDEO CALL IMMERSION */
.call-video-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 2rem;
  overflow: hidden;
  background: #000;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7), inset 0 0 0 1px rgba(255,255,255,0.1);
  z-index: 0;
}

/* Name + timer overlay at bottom of fullscreen video */
.call-video-bottom {
  position: absolute;
  bottom: 7.5rem;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  z-index: 10;
  pointer-events: none;
}

.call-video-name {
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
  letter-spacing: -0.01em;
}

.call-video-timer {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  font-variant-numeric: tabular-nums;
}

/* For video calls, we float the status over the video */
.call-video-container ~ .call-status-wrap {
  position: absolute;
  top: 3rem;
}

.remote-video-wrapper {
  position: absolute;
  inset: 0;
  background: #08080c;
}

.remote-video-wrapper::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
}

.remote-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s;
}

.local-video-mirror {
  transform: scaleX(-1);
}

.remote-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at center, #1e1e2d 0%, #0a0a0f 100%);
  color: #fff;
  z-index: 2;
}

.remote-placeholder-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
  border: 2px solid rgba(255, 255, 255, 0.15);
}

.remote-placeholder-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}

.local-video-pip {
  position: absolute;
  bottom: 140px; /* Above controls */
  right: 1.5rem;
  width: 110px;
  height: 150px;
  border-radius: 1.25rem;
  overflow: hidden;
  background: rgba(20, 20, 30, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.4s;
  z-index: 10;
}

.local-video-pip:hover {
  transform: scale(1.08) translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6);
  border-color: rgba(255, 255, 255, 0.4);
}

.local-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #11111a;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  font-weight: 500;
}

/* VOICE CALL ELEGANCE */
.call-voice-avatar-wrapper {
  position: relative;
  margin: 3rem 0 5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-voice-rings {
  position: absolute;
  inset: -60px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid rgba(var(--color-brand-rgb), 0.6);
  box-shadow: 0 0 30px rgba(var(--color-brand-rgb), 0.2), inset 0 0 20px rgba(var(--color-brand-rgb), 0.2);
  animation: voice-ripple 3.5s cubic-bezier(0.19, 1, 0.22, 1) infinite;
  opacity: 0;
}

.voice-ring-1 { width: 100%; height: 100%; animation-delay: 0s; }
.voice-ring-2 { width: 100%; height: 100%; animation-delay: 1.75s; }

@keyframes voice-ripple {
  0% { transform: scale(0.7); opacity: 1; border-width: 3px; }
  100% { transform: scale(1.6); opacity: 0; border-width: 0px; }
}

.call-voice-avatar {
  position: relative;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.03));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 2;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); box-shadow: 0 35px 60px rgba(0, 0, 0, 0.6), inset 0 0 0 1px rgba(255, 255, 255, 0.2); }
}

/* CONTROLS (Floating Glass Dock) */
.call-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  padding: 1rem 1.75rem;
  background: rgba(20, 20, 30, 0.4);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2rem;
  box-shadow: 0 20px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1);
  z-index: 10;
  position: absolute;
  bottom: 2.5rem;
}

/* Base style for the new NEW squircle button designs */
.call-btn {
  width: 58px;
  height: 58px;
  border-radius: 1.25rem; /* Squircle style instead of full circle */
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.call-btn::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
}

.call-btn:hover::after {
  opacity: 1;
}

.call-btn:active {
  transform: scale(0.88);
}

.call-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 4px;
}

/* Standard Secondary button (Video/Mute when ON) */
.call-btn--secondary {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.call-btn--secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transform: translateY(-4px);
}

/* Muted/Video-Off button design */
.call-btn--muted {
  background: rgba(255, 255, 255, 0);
  color: #fca5a5; /* Soft red to denote off/muted without being as harsh as hangup */
  border: 1px solid rgba(248, 113, 113, 0.4);
  box-shadow: inset 0 0 15px rgba(239, 68, 68, 0.15);
}

.call-btn--muted:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(248, 113, 113, 0.6);
  color: #fecaca;
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(239, 68, 68, 0.15), inset 0 0 15px rgba(239, 68, 68, 0.25);
}

/* Hangup Button - Now a wide pill instead of a circle */
.call-btn--end {
  width: 90px; /* Wide pill */
  height: 58px;
  border-radius: 2rem;
  background: #ef4444;
  color: #fff;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.35);
  border: 1px solid #f87171;
}

.call-btn--end:hover {
  background: #dc2626;
  box-shadow: 0 12px 25px rgba(220, 38, 38, 0.5);
  transform: translateY(-4px);
  border-color: #fca5a5;
}

/* Cancel button for Dialing */
.call-cancel-btn {
  position: absolute;
  bottom: 2.5rem;
  padding: 1.15rem 4rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
  background: rgba(30, 30, 45, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
  cursor: pointer;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 15px 35px rgba(0,0,0,0.3);
  z-index: 10;
}

.call-cancel-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
}

.call-cancel-btn:active {
  transform: scale(0.95);
}

.call-remote-audio {
  position: absolute;
  left: -9999px;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

/* Unmute prompt banner */
.call-unmute-banner {
  position: absolute;
  top: 2rem;
  left: 50%;
  transform: translateX(-50%);
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 100px;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  animation: call-content-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.call-unmute-banner:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.4);
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.4);
}
</style>
